import { create } from 'zustand';

import SocketService from '@src/services/SocketService';

import { EMessageType, IMessage, IReceiverMessageData } from '@src/types';

interface IChatStore {
  selectedReceiverData: IReceiverMessageData;
  publicMessages: IMessage[];
  privateMessages: Map<string, IMessage[]>;
  panelistMessages: IMessage[];
  typingStatus: Map<string, string>; // To keep track for all private chats.
  bannedUserIds: Set<string>;
  setSelectedReceiverData: (messageData: IReceiverMessageData) => void;
  setMessages: (messages: IMessage[], type: EMessageType, id?: string) => void;
  appendMessage: (message: IMessage, chatId?: string) => void;
  deleteMessage: ({ messageId, to }: { messageId: string; to: string }) => void;

  updateMessage: ({
    messageId,
    to,
    updateField,
  }: {
    messageId: string;
    to: string | EMessageType;
    updateField: Partial<IMessage>;
  }) => void;
  updateTypingState: (userId: string, typingMessage: string) => void;
  resetStates: () => void;
  removePrivateChat: (userId: string) => void;

  editingMessageId: string | null;
  setEditingMessageId: (id: string | null) => void;

  editingInitialText: string;
  setEditingInitialText: (text: string) => void;
  removeUserMessages: ({ userId, messageType }: { userId: string; messageType: EMessageType }) => void;
  addBannedUser: (userId: string) => void;
}

export const useChatStore = create<IChatStore>((set, get) => ({
  selectedReceiverData: { key: EMessageType.PUBLIC, value: '' },
  publicMessages: [],
  privateMessages: new Map(),
  panelistMessages: [],
  typingStatus: new Map(),
  bannedUserIds: new Set(),
  editingMessageId: null,
  setEditingMessageId: (id) => set({ editingMessageId: id }),

  editingInitialText: '',
  setEditingInitialText: (text) => set({ editingInitialText: text }),

  addBannedUser: (userId) =>
    set((state) => {
      const newBannedUserIds = new Set(state.bannedUserIds);
      newBannedUserIds.add(userId);
      return { bannedUserIds: newBannedUserIds };
    }),

  setMessages: (messages, type, id) => {
    switch (type) {
      case EMessageType.PUBLIC:
        set({ publicMessages: messages });
        break;
      case EMessageType.PANELIST:
        set({ panelistMessages: messages });
        break;
      case EMessageType.PRIVATE:
        set((state) => {
          if (!id) return { ...state };
          const updatedPrivateMessages = new Map(state.privateMessages);
          updatedPrivateMessages.set(id, messages);
          return { privateMessages: updatedPrivateMessages };
        });

        break;

      default:
        break;
    }
  },

  setSelectedReceiverData: (messageData) => {
    set({ selectedReceiverData: messageData });
  },

  appendMessage: (message) =>
    set((state) => {
      // PUBLIC
      if (message.type === EMessageType.PUBLIC) {
        return { publicMessages: [...state.publicMessages, message] };
      }

      // PANELIST
      if (message.type === EMessageType.PANELIST) {
        return { panelistMessages: [...state.panelistMessages, message] };
      }

      // PRIVATE:
      const chatPartnerId = message.peer_id === SocketService.getSocketId() ? message.to_peer_id : message.peer_id;
      if (!chatPartnerId) return state;
      const updatedPrivateMessages = new Map(state.privateMessages);
      updatedPrivateMessages.set(chatPartnerId, [...(state.privateMessages.get(chatPartnerId) || []), message]);

      return { privateMessages: updatedPrivateMessages };
    }),

  deleteMessage: ({ messageId, to }) =>
    set((state) => {
      // PUBLIC
      if (to === EMessageType.PUBLIC) {
        const updatedPublicMessages = state.publicMessages.filter(({ chatMessageId }) => chatMessageId !== messageId);
        return { publicMessages: updatedPublicMessages };
      }

      // PANELIST
      if (to === EMessageType.PANELIST) {
        const updatedPanelistMessages = state.panelistMessages.filter(
          ({ chatMessageId }) => chatMessageId !== messageId
        );
        return { panelistMessages: updatedPanelistMessages };
      }

      // PRIVATE:
      const updatedPrivateMessages = new Map(state.privateMessages);
      const userMessages = updatedPrivateMessages.get(to) || [];
      updatedPrivateMessages.set(
        to,
        userMessages.filter(({ chatMessageId }) => chatMessageId !== messageId)
      );
      return { privateMessages: updatedPrivateMessages };
    }),

  updateMessage: ({ messageId, to, updateField }) =>
    set((state) => {
      // PUBLIC
      if (to === EMessageType.PUBLIC) {
        return {
          publicMessages: state.publicMessages.map((msg) =>
            msg.chatMessageId === messageId ? { ...msg, ...updateField } : msg
          ),
        };
      }

      // PANELIST
      if (to === EMessageType.PANELIST) {
        return {
          panelistMessages: state.panelistMessages.map((msg) =>
            msg.chatMessageId === messageId ? { ...msg, ...updateField } : msg
          ),
        };
      }

      // PRIVATE:
      const updatedPrivateMessages = new Map(state.privateMessages);
      const userMessages = updatedPrivateMessages.get(to) || [];
      const messageIndex = userMessages.findIndex(({ chatMessageId }) => chatMessageId === messageId);
      const updatedMessages = [...userMessages];
      if (messageIndex > -1) {
        updatedMessages[messageIndex] = { ...updatedMessages[messageIndex], ...updateField };
      }
      updatedPrivateMessages.set(to, updatedMessages);
      return { privateMessages: updatedPrivateMessages };
    }),

  updateTypingState: (userId: string, typingMessage: string) =>
    set((state) => {
      const newTypingMap = new Map(state.typingStatus);
      newTypingMap.set(userId, typingMessage);
      return { typingStatus: newTypingMap };
    }),

  // Reset all states to their default values
  resetStates: () =>
    set(() => ({
      selectedReceiverData: { key: EMessageType.PUBLIC, value: '' },
      publicMessages: [],
      privateMessages: new Map(),
      panelistMessages: [],
      typingStatus: new Map(),
      bannedUserIds: new Set(),
    })),

  // Remove all private messages for the specified user
  removePrivateChat: (userId: string) =>
    set((state) => {
      const updatedPrivateMessages = new Map(state.privateMessages);
      updatedPrivateMessages.delete(userId);
      if (state.selectedReceiverData.key === EMessageType.PRIVATE && state.selectedReceiverData.value === userId) {
        // Reset selected receiver data if the removed chat was the currently selected one
        return {
          selectedReceiverData: { key: EMessageType.PUBLIC, value: '' },
          privateMessages: updatedPrivateMessages,
        };
      }
      return { privateMessages: updatedPrivateMessages };
    }),
  removeUserMessages: ({ userId, messageType }) =>
    set((state) => {
      if (messageType === EMessageType.PUBLIC) {
        const updatedPublicMessages = state.publicMessages.filter(({ peer_id }) => peer_id !== userId);
        return {
          ...state,
          publicMessages: updatedPublicMessages,
        };
      }
      return {
        ...state,
      };
    }),
}));
